import { computed, ref, watch } from 'vue'
import { defineStore } from 'pinia'
import { getToken } from '@/utilities/cookies';
import { MEDIA_ASSET_BASE_URL } from '@/config/constants'
import { useUserStore } from '@/stores/userStore'

import branchService from '@/services/branchService'
import customerService from '@/services/customerService'
import globalCartService from '@/services/globalCartService'
import productCatalogService, { clearInventoryInflight } from "@/services/productCatalogService"

export const useGlobalCatalogStore = defineStore('globalCatalog', () => {
    const userStore = useUserStore();
    const componentToShow = ref('CatalogCategoryTemplates')
    const loading = ref(false);
    const selectedBranch = ref(null)
    const fortifiedEnabled = ref(true) // featureFlag
    const token = getToken();
    const selectedBranchCode = computed(() => selectedBranch.value?.BranchCode || null);
    const user = computed(() => userStore.userDetails);
    const INVENTORY_DEBOUNCE_MS = 50;
    let inventoryBatchQueue = [];
    let inventoryBatchTimer = null;
    let inventoryBatchShippingMethod = null;
    let inventoryBatchRequestDate = null;
    const inventoryRequestSuppressionCount = ref(0);
    const inventoryRequestsEnabled = computed(() => inventoryRequestSuppressionCount.value === 0);
    let inventoryBatchHasForceRefresh = false;

    const branches = ref([]);
    // Request token to cancel stale loadProducts responses when branch/search changes rapidly
    let loadProductsRequestId = 0;
    const userCanViewPricing = computed(() => userStore.userRoles?.includes('PriceViewer') ?? false);

    const getEligibleBranches = async () => {
        //@TODO For hotfix 111823 will revisit this logic to optimize retrieval and avoid unnecessary API calls.
        // const storageKey = `rh_eligiblebranchlist_${userStore.customerCode}`;
        // const branchList = sessionStorage.getItem(storageKey);
        // if (branchList) {
        //     branches.value = JSON.parse(branchList);
        // } else {
        const response = await customerService.getEligibleBranches();
        branches.value = response.data;
        //sessionStorage.setItem(storageKey, JSON.stringify(response.data))
        // }

        // Optimized branch selection logic
        let foundBranch = null;
        const defaultDeliveryBranch = user.value?.UserSettings?.DefaultDeliveryBranch;
        const homeBranch = user.value?.HomeBranch;
        const userLatitude = user.value?.Latitude;
        const userLongitude = user.value?.Longitude;

        // 1. Use DefaultDeliveryBranch if set and not ClosestBranch or HomeBranch
        if (defaultDeliveryBranch && defaultDeliveryBranch !== "ClosestBranch" && defaultDeliveryBranch !== "HomeBranch") {
            foundBranch = branches.value.find(b => b.BranchCode === defaultDeliveryBranch);
        }

        // 2. Use ClosestBranch if DefaultDeliveryBranch is ClosestBranch and user has coordinates
        if (!foundBranch && (defaultDeliveryBranch === "ClosestBranch" || !homeBranch)) {

            // If user location is missing, return first branch as default
            if (
                userLatitude === null || userLatitude === '' ||
                userLongitude === null || userLongitude === ''
            ) {
                return branches.value && branches.value.length > 0 ? branches.value[0] : null;
            }

            let minDistance = Infinity;
            let closestBranch = null;
            branches.value.forEach(branch => {
                const distance = Math.sqrt(
                    Math.pow(branch.Latitude - userLatitude, 2) +
                    Math.pow(branch.Longitude - userLongitude, 2)
                );
                if (distance < minDistance) {
                    minDistance = distance;
                    closestBranch = branch;
                }
            });
            foundBranch = closestBranch;
        }

        // 3. Use HomeBranch if DefaultDeliveryBranch is HomeBranch or if HomeBranch is set
        if (!foundBranch && homeBranch) {
            foundBranch = branches.value.find(b => b.BranchCode === homeBranch);
        }

        // 4. Fallback to first branch if nothing else matches
        selectedBranch.value = foundBranch || branches.value[0];
    }
    // Promise for the initial branch resolution so callers (e.g. the global search on
    // page load) can await the branch before searching instead of racing it. Racing it
    // caused an unfiltered search followed by an auto-triggered branch-filtered re-search.
    const branchesReady = getEligibleBranches();

    const setSelectedBranch = (branchCode) => {
        if ( branchCode && branchCode !== selectedBranchCode.value) {
            const branchDetails = branches.value.find(branch => branch.BranchCode === branchCode) || null;
            if ( branchDetails ) {
                selectedBranch.value = branchDetails;
            }
        }
    }

    const customerShipToAddresses = ref([])
    const loadCustomerShipToAddresses = async () => {
        // let key = `rh_customerShipTo_${userStore.customerCode}`;
        // const existingCustomerShipToAddresses = sessionStorage.getItem(key);
        // if (existingCustomerShipToAddresses) {
        //     customerShipToAddresses.value = JSON.parse(existingCustomerShipToAddresses);
        // } else {
        // @TODO For hotfix 111823 will revisit this logic to optimize user details retrieval and avoid unnecessary API calls. 
        try {
            const response = await customerService.getShipToAddresses();
            if (response.success) {
                customerShipToAddresses.value = response.data;
                //sessionStorage.setItem(key, JSON.stringify(response.data));
            } else {
                console.warn("Unable to load customer ship to addresses.");
            }
        } catch (e) {
            console.error("Customer ship to addresses were not loaded", e);
        }
        //}

    }
    loadCustomerShipToAddresses();
    const fetchJobAccounts = loadCustomerShipToAddresses;

    const branchShipToAddresses = ref({});
    const loadBranchShipToAddresses = async () => {
        // let key = `rh_branchShipTo_${userStore.customerCode}`;
        // const existingBranchShipToAddresses = sessionStorage.getItem(key);
        // if (existingBranchShipToAddresses) {
        //     branchShipToAddresses.value = JSON.parse(existingBranchShipToAddresses);
        // } else {
        // @TODO For hotfix 111823 will revisit this logic to optimize retrieval and avoid unnecessary API calls.
        try {
            var response = await branchService.getShipToAddressesForBranches();
            if (response.success) {
                branchShipToAddresses.value = response.data;
                //sessionStorage.setItem(key, JSON.stringify(response.data));
            } else {
                console.warn("Branch ship to addresses were not loaded");
            }
        } catch (e) {
            console.error("Branch Ship To Addresses failed to load", e);
        }
        //}
    }
    loadBranchShipToAddresses();

    const shipToAddressesForSelectedBranch = computed(() => {
        if (selectedBranchCode.value) {
            const shipToSequenceNumbers = branchShipToAddresses.value[selectedBranchCode.value] ?? null;
            if (shipToSequenceNumbers === null || shipToSequenceNumbers.length === 0) {
                console.warn(`${selectedBranchCode.value} has no ship to sequence numbers found`);
                return [];
            }
            return customerShipToAddresses.value.filter(c => shipToSequenceNumbers.some(s => s == c.ShipToSequenceId));
        }
        return [];
    });

    const jobAccounts = ref([]);
    const selectedShipToSequenceId = ref(null);
    const selectedJobAccount = ref({})
    watch(
        () => shipToAddressesForSelectedBranch.value,
        () => {
            jobAccounts.value = shipToAddressesForSelectedBranch.value;
            selectedShipToSequenceId.value = shipToAddressesForSelectedBranch.value?.length ? shipToAddressesForSelectedBranch.value[0].ShipToSequenceId : null;
            selectedJobAccount.value = customerShipToAddresses.value.find(c => c.ShipToSequenceId == selectedShipToSequenceId.value) ?? null;
        }
    )

    const userRoles = computed(() => userStore.userRoles);

    const categories = ref([]);
    const manufacturers = ref([]);
    const selectedTemplates = ref([]);
    const selectedCategories = ref([]);
    const selectedManufacturers = ref([]);
    const selectedListTypes = ref([]);
    const selectedColors = ref([]);
    const selectedSizes = ref([]);

    const listTypesFilters = [
        "Roof Hub Recommends",
        "Fortified Eligible",
        "Promo Items",
        "Generics"
    ];
    const listTypes = computed(() => fortifiedEnabled.value ? listTypesFilters : listTypesFilters.filter(t => t !== "Fortified Eligible"));

    const error = ref(null);
    const loadRoofHubCategories = async (orderCatalogFlag = false) => {
        if (!selectedBranchCode.value) {
            await getEligibleBranches();
        }
        const storageKey = selectedBranchCode.value ? `rh_mth_cats_${selectedBranchCode.value}` : 'rh_categories';
        const existingCategories = sessionStorage.getItem(storageKey);
        if (existingCategories && !orderCatalogFlag) {
            categories.value = JSON.parse(existingCategories);
        } else {
            try {
                loading.value = true;
                let categoryList = [];
                const response = await productCatalogService.fetchProductCategoriesByBranch(selectedBranchCode.value, orderCatalogFlag);
                categoryList = response.data.Data;
                categories.value = categoryList;
                sessionStorage.setItem(storageKey, JSON.stringify(categoryList))
            } catch (e) {
                console.error(e)
            } finally {
                loading.value = false;
            }
        }
        categories.value.sort((a, b) => a.DisplaySequence - b.DisplaySequence);
    }

    const shouldLoadCategories = () => {
        return !window.location.pathname.startsWith('/profile/')
    }

    if (shouldLoadCategories()) {
        loadRoofHubCategories()
    }

    const getManufacturers = async () => {
        try {
            if (!selectedBranchCode.value) {
                await getEligibleBranches();
            }
            const response = await productCatalogService.fetchManufacturersByBranch(selectedBranchCode.value, 1000);
            manufacturers.value = response.data.Data;
        } catch (e) {
            console.error(e)
        } finally {
            loading.value = false;
        }
    }
    getManufacturers();

    const setIsSelectedForCategories = (category, isSelected) => {
        category.isSelected = isSelected;
        if (category.Categories.length > 0) {
            for (const cat of category.Categories) {
                setIsSelectedForCategories(cat, isSelected)
            }
        }
    }

    const getSelectedCategories = category => {
        if (category.isSelected) {
            selectedCategories.value.push(category)
        } else if (category.Categories.length) {
            for (const cat of category.Categories) {
                getSelectedCategories(cat);
            }
        }
    };

    watch(
        () => categories.value,
        () => {
            selectedCategories.value = []
            for (const cat of categories.value) {
                getSelectedCategories(cat)
            }
        }, { deep: true }
    );

    // Filter for loading products
    const facetFilters = computed(() => {
        const filters = [];
        if (selectedCategories.value.length) {
            filters.push({
                name: "Categories/FullCategoryName",
                values: selectedCategories.value.map(category => category.FullCategoryName)
            })
        }

        if (selectedManufacturers.value.length > 0) {
            filters.push({
                name: "Manufacturer",
                values: selectedManufacturers.value.map(m => m.Manufacturer)
            });
        }

        if (selectedListTypes.value.includes('Promo Items')) {
        } else if (selectedListTypes.value.includes('Fortified Eligible')) {
            filters.push({ name: "Certifications", values: ["Fortified"] });
        }

        if (selectedListTypes.value.includes('Generics')) {
            filters.push({ name: "IsGeneric", values: [true] });
        }

        if (selectedColors.value.length > 0) {
            filters.push({
                name: "Items/Color",
                values: [...selectedColors.value]
            });
        }

        if (selectedSizes.value.length > 0) {
            filters.push({
                name: "Items/Size",
                values: [...selectedSizes.value]
            });
        }

        return filters;
    });

    const products = ref({
        products: []
    });

    // Facet option counts returned by the search API, keyed by facet name then lowercased option value.
    // Used to display the number of matching products next to each filter option.
    const facetCounts = ref({});

    // Color and Size filter options are driven entirely by the search results - there is no
    // separate lookup API for them, so the options are taken from the facets the search returns.
    const colorOptions = ref([]);
    const sizeOptions = ref([]);
    const facetOptionLimit = 10;

    const buildFacetOptions = (facets, facetName) => {
        const facet = (facets || []).find(f => (f.name ?? f.Name) === facetName);
        const options = facet?.options ?? facet?.Options ?? [];
        return options
            .map(option => ({
                name: option.name ?? option.Name,
                count: option.count ?? option.Count ?? 0
            }))
            .filter(option => option.name !== null && option.name !== undefined && option.name !== '')
            .sort((a, b) => b.count - a.count)
            .slice(0, facetOptionLimit);
    };

    const updateFacetCounts = (facets) => {
        const map = {};
        (facets || []).forEach(facet => {
            const facetName = facet.name ?? facet.Name;
            if (!facetName) return;
            const options = facet.options ?? facet.Options ?? [];
            const optionCounts = {};
            options.forEach(option => {
                const optionName = option.name ?? option.Name;
                if (optionName === null || optionName === undefined) return;
                optionCounts[String(optionName).toLowerCase()] = option.count ?? option.Count ?? 0;
            });
            map[facetName] = optionCounts;
        });
        facetCounts.value = map;
    };

    const clearColorSizeOptions = () => {
        colorOptions.value = [];
        sizeOptions.value = [];
    };

    // Color/Size options are results driven: they are built from the current search (term plus the
    // other active filters). Each list is requested without its own filter so the user can still
    // widen the selection once an option is checked.
    let colorSizeFacetsRequestId = 0;
    const loadColorSizeFacets = async (basePayload) => {
        const requestId = ++colorSizeFacetsRequestId;
        const facetOnlyPayload = (excludedFacet) => ({
            ...basePayload,
            facetFilters: basePayload.facetFilters.filter(f => f.name !== excludedFacet),
            options: { ...basePayload.options, includeAssetTypes: [], includeFrequentItems: false, includePromotions: false, includeRecommendations: false },
            skip: 0,
            take: 1,
            useFacets: true
        });

        try {
            const [colorResponse, sizeResponse] = await Promise.all([
                productCatalogService.searchProducts(facetOnlyPayload('Items/Color')),
                productCatalogService.searchProducts(facetOnlyPayload('Items/Size'))
            ]);
            if (requestId !== colorSizeFacetsRequestId) return;

            const colorData = colorResponse?.data?.Data;
            const sizeData = sizeResponse?.data?.Data;
            colorOptions.value = buildFacetOptions(colorData?.facets ?? colorData?.Facets, 'Items/Color');
            sizeOptions.value = buildFacetOptions(sizeData?.facets ?? sizeData?.Facets, 'Items/Size');
        } catch (e) {
            console.error('Color/Size facets failed to load', e);
        }
    };

    // The result set is wiped on branch change, so the previous branch's counts are stale and
    // must go with it. Options stay enabled while the new counts are being fetched.
    const clearFacetCounts = () => {
        facetCounts.value = {};
    };

    const getFacetCount = (facetName, optionValue) => {
        if (optionValue === null || optionValue === undefined) return null;
        const counts = facetCounts.value[facetName];
        if (!counts) return null;
        return counts[String(optionValue).toLowerCase()] ?? 0;
    };

    const getCategoryProductCount = category => getFacetCount('Categories/FullCategoryName', category?.FullCategoryName);
    const getManufacturerProductCount = manufacturer => getFacetCount('Manufacturer', manufacturer?.Manufacturer);

    // Facet counts are loaded with their own lightweight, branch-scoped request instead of
    // piggybacking on loadProducts. This keeps the counts available as soon as the filters
    // render (even before any search has run, e.g. landing straight on the Products page or
    // after a branch swap) and keeps them independent of the filters the user has selected,
    // so an option showing (0) is still a valid, selectable choice.
    let facetCountsRequestId = 0;
    const loadFacetCounts = async () => {
        // Counts are branch scoped, so make sure the branch has resolved first
        if (!selectedBranchCode.value) {
            await branchesReady;
        }

        const requestedBranch = selectedBranchCode.value;
        const requestId = ++facetCountsRequestId;

        const payload = {
            branchCode: requestedBranch || '',
            excludeNotAvailableAtBranch: true,
            facetFilters: [],
            options: {
                includeAssetTypes: [],
                includeFrequentItems: false,
                includePromotions: false,
                includeRecommendations: false,
                productIds: [],
                recommendationsOnly: false
            },
            searchTerm: productSearchTerm.value,
            skip: 0,
            take: 1,
            useFacets: true
        };

        try {
            const response = await productCatalogService.searchProducts(payload);

            // Discard stale responses (branch/search changed while in flight)
            if (requestId !== facetCountsRequestId) return;
            if (requestedBranch !== selectedBranchCode.value) return;

            const data = response?.data?.Data;
            updateFacetCounts(data?.facets ?? data?.Facets);
        } catch (e) {
            console.error('Facet counts failed to load', e);
        }
    };

    const productsPerPage = 20;
    const productSearchTerm = ref('')
    const productIds = ref([])
    // During page init several watchers resolve in sequence (branch, then job account, then the
    // ship-to sequence) and each one asks for the same search, which fired five identical
    // requests. The requestId guard below only discards duplicate *responses* - these two let
    // identical calls that overlap in time share a single HTTP request instead.
    let inFlightSearchKey = null;
    let inFlightSearch = null;

    const loadProducts = async (pageNo = 1) => {
        const requestedBranch = selectedBranchCode.value;

        const payload = {
            branchCode: requestedBranch || '',
            excludeNotAvailableAtBranch: true,
            facetFilters: facetFilters.value,
            options: {
                includeAssetTypes: ["ProductPrimaryImage", "ItemPrimaryImage", "ItemAlternateImage", "ItemSellSheet"],
                includeFrequentItems: !!requestedBranch,
                includePromotions: !!requestedBranch,
                includeRecommendations: !!requestedBranch,
                productIds: productIds.value,
                recommendationsOnly: selectedListTypes.value.includes('Roof Hub Recommends')
            },
            searchTerm: productSearchTerm.value,
            skip: (pageNo - 1) * productsPerPage,
            take: productsPerPage,
            useFacets: facetFilters.value.length > 0
        };
        const isPromotions = selectedListTypes.value.includes('Promo Items');

        // Identical request already on the wire - reuse it rather than firing a duplicate.
        const searchKey = JSON.stringify([payload, isPromotions]);
        if (inFlightSearch && inFlightSearchKey === searchKey) {
            return inFlightSearch;
        }

        const requestId = ++loadProductsRequestId;

        const runSearch = async () => {
            try {
                componentToShow.value = 'Products';
                loading.value = true;

                // Clear stale products immediately so the UI doesn't show old branch's images
                products.value = { products: [] };

                const response = await productCatalogService.searchProducts(payload, isPromotions);

                // Discard response if a newer request started or branch changed mid-flight
                if (requestId !== loadProductsRequestId) return;
                if (requestedBranch !== selectedBranchCode.value) return;

                products.value = response.data.Data;

                if (products.value?.products?.length) {
                    getAllProductPricing();
                    loadInventoryStates(bomItemDeliveryType.value || null);
                    loadColorSizeFacets(payload);
                } else {
                    componentToShow.value = 'NoSearchResults';
                    if (!selectedColors.value.length && !selectedSizes.value.length) {
                        clearColorSizeOptions();
                    }
                }
            } catch (e) {
                if (requestId === loadProductsRequestId) {
                    console.error(e);
                    error.value = true;
                }
            } finally {
                if (requestId === loadProductsRequestId) {
                    loading.value = false;
                }
                // Only clear if we are still the current in-flight search; a newer, differently
                // keyed request may have replaced us while this one was running.
                if (inFlightSearchKey === searchKey) {
                    inFlightSearchKey = null;
                    inFlightSearch = null;
                }
            }
        };

        inFlightSearchKey = searchKey;
        inFlightSearch = runSearch();
        return inFlightSearch;
    };

    // No search means no results, so Color/Size filters must not linger (e.g. reopening the catalog)
    watch(
        () => componentToShow.value,
        (value) => {
            if (value !== 'CatalogCategoryTemplates') return;
            colorSizeFacetsRequestId++;
            clearColorSizeOptions();
            if (selectedColors.value.length) selectedColors.value = [];
            if (selectedSizes.value.length) selectedSizes.value = [];
        }
    );

    // Keep the filter product counts in sync with the active branch and search term.
    // immediate: true covers direct navigation to the Products page, where the counts must be
    // present on first render rather than only after the first product search completes.
    watch(
        () => [selectedBranchCode.value, productSearchTerm.value],
        () => {
            if (!shouldLoadCategories()) return;
            // Counts are branch scoped; wait for the branch to resolve on first load
            if (!selectedBranchCode.value) return;
            loadFacetCounts();
        },
        { immediate: true }
    );

    const getProductDetailsById = async (productId) => {
        try {
            const payload = {
                BranchCode: selectedBranchCode.value,
                ExcludeProductsNotAvailable: selectedBranchCode.value ? true : false,
                ProductId: productId
            }
            const response = await productCatalogService.getProductDetailsById(payload);
            return response.data.Data;
        } catch (e) {
            throw e;
        }
    }
    
    const productsPricing = ref([]);
    // Keep only one pricing row per ItemCode to avoid stale .find() hits
    const upsertPricingEntry = (entry) => {
        if (!entry || !entry.ItemCode) return;
        const idx = productsPricing.value.findIndex(p => p.ItemCode === entry.ItemCode);
        if (idx !== -1) {
            productsPricing.value.splice(idx, 1, entry);
        } else {
            productsPricing.value.push(entry);
        }
    };
    const getAllProductPricing = async () => {
        try {
            if (!userCanViewPricing.value) {
                return 0;
            }
            if (products.value.products?.length) {
                let itemCodes = [];
                for (const product of products.value.products) {
                    if (product.variants.length) {
                        const variant = product.variants[0];
                        const uom = variant.uoms.find(uom => uom.isDefaultMeasure === true)?.name
                        itemCodes.push({
                            orderItemIndex: 0,
                            ItemCode: variant.itemCode,
                            Quantity: null,
                            UOM: uom,
                            Option: ""
                        })
                    }
                }
                const payload = {
                    itemCodes: itemCodes,
                    branchCode: selectedBranchCode.value,
                    customerCode: userStore.customerCode,
                    shipToSequenceNumber: selectedJobAccount.value.ShipToSequenceId
                }
                const response = await productCatalogService.getProductPricing(payload);
                if ( response?.success && response.data?.Data?.Pricing?.length) {
                    productsPricing.value = response.data.Data.Pricing;
                }
            }
        } catch (e) {
            console.error(e);
        }
    }

    // Called from inside loadProducts
    const deliveryType = {
        "PickupAtBranch": 1,
        "Delivery": 2,
    }
    
    // Helper function to normalize delivery type strings to enum values
    const normalizeDeliveryType = (shippingMethod) => {        
        if (!shippingMethod) return deliveryType.PickupAtBranch;
        const normalized = shippingMethod.toUpperCase();
        return normalized === 'WILLCALL' ? deliveryType.PickupAtBranch 
             : normalized === 'DELIVERY' ? deliveryType.Delivery 
             : deliveryType.PickupAtBranch;
    };
    
    const productInventoryLevelStatus = ref([]);
    const inventoryStatusRequestDate = ref(null);
    const clearQueuedInventoryRequests = () => {
        productInventoryLevelStatus.value = [];
        clearInventoryInflight();
        if (inventoryBatchTimer) {
            clearTimeout(inventoryBatchTimer);
            inventoryBatchTimer = null;
        }
        inventoryBatchQueue = [];
        inventoryBatchShippingMethod = null;
        inventoryBatchRequestDate = null;
    };

    const suspendInventoryRequests = () => {
        inventoryRequestSuppressionCount.value += 1;
        clearQueuedInventoryRequests();
    };

    const resumeInventoryRequests = () => {
        inventoryRequestSuppressionCount.value = Math.max(0, inventoryRequestSuppressionCount.value - 1);
    };

    const upsertInventoryStatusEntries = (entries) => {
        if (!Array.isArray(entries)) return;
        for (const entry of entries) {
            if (!entry || !entry.ItemCode) continue;
            const idx = productInventoryLevelStatus.value.findIndex(s => s.ItemCode === entry.ItemCode);
            if (idx !== -1) {
                productInventoryLevelStatus.value.splice(idx, 1, entry);
            } else {
                productInventoryLevelStatus.value.push(entry);
            }
        }
    };

    const loadInventoryStates = async (shippingMethod = null) => {
        if (!inventoryRequestsEnabled.value) return;

        let itemCodes = [];
        for (const p of products.value.products) {
            if (p.variants.length) {
                for (const item of p.variants) {
                    itemCodes.push(item.itemCode)
                }
            }
        }
        const payload = {
            originBranchCode: selectedBranchCode.value,
            deliveryType: normalizeDeliveryType(shippingMethod),
            specificItemCodes: itemCodes,
            requestDate: inventoryStatusRequestDate.value || new Date().toISOString()
        };
        if (selectedBranchCode.value && itemCodes.length) {
            const response = await productCatalogService.getProductInventoryLevels(payload);
            if (response?.data) {
                upsertInventoryStatusEntries(response.data);
            }
        }
    }



    const flushInventoryBatch = async () => {
        inventoryBatchTimer = null;

        if (!inventoryRequestsEnabled.value) {
            inventoryBatchQueue = [];
            return;
        }

        const forceRefresh = inventoryBatchHasForceRefresh;
        inventoryBatchHasForceRefresh = false;

        const alreadyLoaded = new Set(
            productInventoryLevelStatus.value.map(s => s.ItemCode)
        );
        const toFetch = [...new Set(inventoryBatchQueue)].filter(
            code => forceRefresh || !alreadyLoaded.has(code)
        );
        inventoryBatchQueue = [];

        if (!toFetch.length || !selectedBranchCode.value) return;

        const payload = {
            originBranchCode: selectedBranchCode.value,
            deliveryType: normalizeDeliveryType(inventoryBatchShippingMethod),
            specificItemCodes: toFetch,
            requestDate: inventoryBatchRequestDate
                || inventoryStatusRequestDate.value
                || new Date().toISOString()
        };

        try {
            const response = await productCatalogService.getProductInventoryLevels(payload, null, forceRefresh);
            if (response?.data?.length) {
                upsertInventoryStatusEntries(response.data);
            }
        } catch (e) {
        }
    };

    const getProductInventoryLevel = (itemCode, requestDate = null, shippingMethod = null, forceRefresh = false) => {
        if (!inventoryRequestsEnabled.value || !itemCode) return;
        
        // If force refresh, remove existing inventory data for this item to ensure fresh fetch
        if (forceRefresh) {
            const idx = productInventoryLevelStatus.value.findIndex(s => s.ItemCode === itemCode);
            if (idx !== -1) {
                productInventoryLevelStatus.value.splice(idx, 1);
            }
            inventoryBatchHasForceRefresh = true;
        }
        
        inventoryBatchQueue.push(itemCode);
        inventoryBatchRequestDate = requestDate || inventoryBatchRequestDate;
        inventoryBatchShippingMethod = shippingMethod || inventoryBatchShippingMethod;
        if (inventoryBatchTimer) clearTimeout(inventoryBatchTimer);
        inventoryBatchTimer = setTimeout(flushInventoryBatch, INVENTORY_DEBOUNCE_MS);
    };

    watch(
        () => selectedTemplates.value,
        () => {
            productIds.value = [];
            if (selectedTemplates.value.length) {
                for (const template of selectedTemplates.value) {
                    for (const item of template.items) {
                        const itemOptions = JSON.parse(item.ItemOptions);
                        productIds.value.push(itemOptions.ProductId);
                    }
                }
            }
        }, { deep: true });

    /**
     * The function `getProductPricing` retrieves pricing information for a specific item based on the
     * item code and unit of measurement provided.
     * @param itemCode - String.
     * @param uom - "Unit of Measure." String
     * @returns pricing information for the specified item code and unit of measure (UOM)
     */
    const getProductPricing = async (itemCode, uom) => {
        if (!userCanViewPricing.value) {
            return 0;
        }
        const payload = {
            itemCodes: [
                {
                    orderItemIndex: 0,
                    ItemCode: itemCode,
                    Quantity: 1,
                    UOM: uom,
                    Option: ""
                }
            ],
            branchCode: selectedBranchCode.value,
            customerCode: userStore.customerCode,
            shipToSequenceNumber: selectedJobAccount.value.ShipToSequenceId
        }
        const response = await productCatalogService.getProductPricing(payload);
        if (!response?.success) {
            return 0;
        }
        const { Pricing: pricing } = response.data.Data;
        //productsPricing.value.push(pricing[0]);
        upsertPricingEntry(pricing[0]);
        return pricing[0];
    }

    /**
     * Batch fetch pricing for multiple items in a single API call
     * @param items - Array of objects with { itemCode, uom }
     * @returns Array of pricing data
     */
    const getBatchProductPricing = async (items) => {
        if (!userCanViewPricing.value || !items || items.length === 0) {
            return [];
        }
        
        const payload = {
            itemCodes: items.map((item, index) => ({
                orderItemIndex: index,
                ItemCode: item.itemCode,
                Quantity: 1,
                UOM: item.uom,
                Option: ""
            })),
            branchCode: selectedBranchCode.value,
            customerCode: userStore.customerCode,
            shipToSequenceNumber: selectedJobAccount.value.ShipToSequenceId
        };
        
        const response = await productCatalogService.getProductPricing(payload);
        if (!response?.success) {
            return [];
        }
        
        const { Pricing: pricing } = response.data.Data;
        
        // Update store with all pricing entries
        if (Array.isArray(pricing)) {
            pricing.forEach(priceData => {
                upsertPricingEntry(priceData);
            });
        }
        
        return pricing || [];
    }

    // Will load products according to the change in selected filter items
    // Also filters are computed accordingly
    watch(
        () => [
            selectedTemplates.value,
            selectedCategories.value,
            selectedManufacturers.value,
            selectedListTypes.value,
            selectedColors.value,
            selectedSizes.value,
            selectedBranch.value,
            selectedJobAccount.value
        ],
        (
            [templates, categories, manufacturers, listTypes, colors, sizes, selectedBranch, selectedJobAccount],
            [prevTemplates, prevCategories, prevManufacturers, prevListTypes, prevColors, prevSizes, prevSelectedBranch, prevSelectedJobAccount],
        ) => {
            if (selectedBranch !== prevSelectedBranch || selectedJobAccount !== prevSelectedJobAccount) {
                if (componentToShow.value === "Products")
                    loadProducts();
            } else if (templates.length === 0 &&
                categories.length === 0 &&
                manufacturers.length === 0 &&
                listTypes.length === 0 &&
                colors.length === 0 &&
                sizes.length === 0 && productSearchTerm.value === '') {
                // A product detail view opened deliberately (e.g. from the global quick search)
                // must survive branch/job-account resolution on page load. Only reset to the
                // category list when we are not intentionally showing a product.
                if (componentToShow.value !== 'ProductDetail') {
                    componentToShow.value = "CatalogCategoryTemplates";
                }
            } else {
                // If user is viewing ProductDetail and filters changed, navigate back to Products list
                // This prevents getProductDetailsById from being called unnecessarily
                if (componentToShow.value === 'ProductDetail') {
                    componentToShow.value = 'Products';
                }
                loadProducts();
            }
        },
        { deep: true }
    );

    // Opt-out for views that re-resolve their own BOM rows on a branch change (order wizard uses
    // switch-context for this), so the branch watcher below skips its duplicate byIds re-sync.
    const suppressExternalCartBranchRefresh = ref(false);

    // Re-fetch selected product and refresh price/inventory when branch or job account changes
    watch(
        () => [selectedBranchCode.value, selectedJobAccount.value?.ShipToSequenceId],
        async ([newBranch, newShipTo], [prevBranch, prevShipTo]) => {
            const branchChanged = newBranch !== prevBranch;
            const jobChanged = newShipTo !== prevShipTo;    
            if (branchChanged) {
                // Clear cached price & inventory tied to previous branch
                productsPricing.value = [];
                productInventoryLevelStatus.value = [];
                clearFacetCounts();
                clearInventoryInflight();
                if (inventoryBatchTimer) clearTimeout(inventoryBatchTimer);
                inventoryBatchTimer = null;
                inventoryBatchQueue = [];
                inventoryBatchRequestDate = null;
                inventoryBatchShippingMethod = null;
                inventoryBatchHasForceRefresh = false;

                // Manufacturer facet list is branch-scoped (bybranchcode API). Reload on swap
                // so CatalogFilters matches heritage RHManufacturerFilter watch. Skip initial
                // null→branch resolve (store already calls getManufacturers on init).
                if (newBranch && prevBranch) {
                    await getManufacturers();
                    const validNames = new Set(
                        (manufacturers.value || []).map(m => m?.Manufacturer).filter(Boolean)
                    );
                    selectedManufacturers.value = selectedManufacturers.value.filter(
                        m => validNames.has(m?.Manufacturer)
                    );

                    // Color/Size options come from the previous branch's search results, so both the
                    // options and any selection made from them are stale after a branch swap.
                    selectedColors.value = [];
                    selectedSizes.value = [];
                    colorOptions.value = [];
                    sizeOptions.value = [];
                }
            } else if (jobChanged) {
                // Clear cached price tied to previous job account
                productsPricing.value = [];
            }

            // If user is viewing Product Detail, refresh its details with updated branch filters
            if (componentToShow.value === 'ProductDetail' && selectedProduct.value?.productId) {
                await refreshSelectedProduct();
            }

            // Re-sync BOM items' availability on branch change to avoid stale flags.
            // Views that re-resolve their own rows on a branch change (order wizard, via
            // switch-context) opt out so the same products are not fetched twice.
            if (branchChanged && externalCartForBOM.value.length > 0 && !suppressExternalCartBranchRefresh.value) {
                try {
                    const productIds = [...new Set(
                        externalCartForBOM.value
                            .map(c => c.productId || c.ProductId)
                            .filter(id => id != null)
                    )];
                    if (productIds.length) {
                        const payload = {
                            branchCode: newBranch,
                            productIds,
                            ExcludeVariantsNotAtBranch: false,
                        };
                        const resp = await productCatalogService.fetchProductsByIds(payload, token);
                        const freshProducts = (resp?.success && Array.isArray(resp.data?.Data)) ? resp.data.Data : [];
                        const freshByPid = new Map(freshProducts.map(p => [p.productId || p.ProductId, p]));

                        for (const cartItem of externalCartForBOM.value) {
                            const pid = cartItem.productId || cartItem.ProductId;
                            const fresh = freshByPid.get(pid);
                            if (!fresh) continue;
                            const freshVariants = fresh.variants || fresh.Variants || [];
                            // Filter to branch-available variants
                            const available = selectedBranchCode.value
                                ? freshVariants.filter(v => v.availableAtBranch)
                                : freshVariants;
                            // Re-resolve selected variant from fresh list
                            const prevSV = (cartItem.selectedVariant && typeof cartItem.selectedVariant === 'object')
                                ? cartItem.selectedVariant
                                : null;
                            const prevCode = prevSV
                                ? (prevSV.itemCode || prevSV.ItemCode)
                                : (cartItem.itemCode || cartItem.ItemCode);
                            const matched = prevCode
                                ? available.find(v => (v.itemCode || v.ItemCode) === prevCode)
                                : null;
                            const resolvedVariant = matched || (available.length > 0 ? available[0] : null);
                            const resolvedAvail = resolvedVariant
                                ? Boolean(resolvedVariant.availableAtBranch ?? resolvedVariant.AvailableAtBranch ?? false)
                                : false;
                            // Sync all four casing variants + selectedVariant object
                            cartItem.selectedVariant = resolvedVariant || cartItem.selectedVariant;
                            cartItem.isAvailableAtBranch = resolvedAvail;
                            cartItem.IsAvailableAtBranch = resolvedAvail;
                            cartItem.availableAtBranch = resolvedAvail;
                            cartItem.AvailableAtBranch = resolvedAvail;
                            // Refresh variants array for the BOM dropdown
                            cartItem.variants = available;
                        }
                    }
                } catch (e) {
                    console.error('Failed to re-sync BOM items on branch change:', e);
                }
            }
        }
    );

    const clearProductSearch = () => {
        productSearchTerm.value = '';
        // Keep showing products if any filter is active (not just categories)
        if (selectedCategories.value.length || 
            selectedTemplates.value.length || 
            selectedManufacturers.value.length || 
            selectedListTypes.value.length ||
            selectedColors.value.length ||
            selectedSizes.value.length) {
            loadProducts()
        } else {
            componentToShow.value = 'CatalogCategoryTemplates'
        }
    };

    const clearAllSelectedCategories = (category = null) => {
        if (!category) {
            for (const cat of categories.value) {
                clearAllSelectedCategories(cat)
            }
        } else {
            category.isSelected = false;
            category.isExpanded = false;
            category.isIndeterminate = false;
            if (category.Categories.length) {
                for (const cat of category.Categories) {
                    clearAllSelectedCategories(cat)
                }
            }
        }
    }

    const clearSelectedFilters = () => {
        selectedTemplates.value = [];
        selectedCategories.value = [];
        selectedListTypes.value = [];
        selectedManufacturers.value = [];
        selectedColors.value = [];
        selectedSizes.value = [];
        clearAllSelectedCategories()
    }

    const clearAllFilters = () => {
        clearSelectedFilters();
        clearProductSearch();
    }

    // Cart
    const globalCart = ref({
        branchCode: '',
        cartDetails: [],
        cardModified: null,
        cardCreated: null,
        cartId: null,
        customerAccount: userStore.customerCode,
        shipToSequenceNumber: selectedJobAccount.value.ShipToSequenceId,
        sourceSystem: 0, // ROOFHUB
    });
    const globalCartItems = computed(() => globalCart.value.cartDetails)
    const addItemToCart = async (product, selectedVariant) => {
        const newProduct = {
            itemId: selectedVariant?.itemId,
            agilitySize: selectedVariant?.agilitySize ?? '',
            category: product.category,
            itemCode: selectedVariant?.itemCode ?? '',
            lineNumber: globalCartItems.value.length + 1,
            manufacturer: product.manufacturer,
            productId: product.productId,
            webName: product.webName,
            options: product.options,
        };
        globalCart.value.branchCode = selectedBranchCode.value;
        globalCart.value.cartDetails.push(newProduct);
        await globalCartService.saveUserCart(globalCart.value);
    }

    const getCartItems = async () => {
        const response = await globalCartService.getUserCart();
        if (response.data.Data) {
            globalCart.value = response.data.Data;
        }
    }

    const cartItems = ref([]);
    const fetchCartItems = async (productIds) => {
        loading.value = true;
        error.value = false;
        try {
            const payload = {
                branchCode: selectedBranchCode.value,
                productIds,
                ExcludeVariantsNotAtBranch: false,
            };
            const response = await productCatalogService.fetchProductsByIds(payload, token);
            const productsInCart = response.data?.Data;
            if (response.success) {
                if (Array.isArray(productsInCart)) {
                    const cartItemsList = [];
                    productsInCart.forEach((product) => {
                        const matchedCartItems = Array.isArray(globalCartItems.value)
                            ? globalCartItems.value.filter(cartItem => cartItem.productId === product.productId)
                            : [];

                        const category = determineProductCategoryFn(product);

                        matchedCartItems.forEach((matchedCartItem) => {
                            const matchedVariant = Array.isArray(product.variants)
                                ? product.variants.find(variant => variant.itemCode === matchedCartItem.itemCode)
                                : {};

                            const imageUrl = matchedVariant?.mediaAssets?.[0]?.assetUri
                                ? `${MEDIA_ASSET_BASE_URL}/${matchedVariant.mediaAssets[0].assetUri}`
                                : '';

                            cartItemsList.push({
                                id: product.productId,
                                name: product.webName,
                                manufacturer: product.manufacturer,
                                image: imageUrl,
                                agilitySize: matchedVariant?.agilitySize || '',
                                sizeName: matchedVariant?.sizeName || '',
                                itemCode: matchedVariant?.itemCode || '',
                                category: category || '',
                                color: matchedVariant?.colorName || '',
                                colorName: matchedVariant?.colorName || '',
                                option: matchedVariant?.colorName || matchedVariant?.agilitySize || '',
                                uom: matchedVariant?.uoms?.find(uom => uom.isDefaultMeasure)?.name || '',
                                productVariant: matchedVariant,
                                matchedProduct: product,
                                productAvailable: Array.isArray(product.variants)
                                    ? product.variants.some(v => v.availableAtBranch)
                                    : false,
                                variantAvailable: matchedVariant?.availableAtBranch ?? false,
                                imageUrl: imageUrl,
                                productDetails: product
                            });
                        });
                    });

                    cartItems.value = cartItemsList;
                }
            } else {
                error.value = true;
            }
        } catch (err) {
            error.value = true;
        } finally {
            loading.value = false;
        }
    };

    const determineProductCategoryFn = (product) => {
        const getDeepestCategoryName = (category) => {
            if (!category) return null;
            if (Array.isArray(category.categories) && category.categories.length > 0) {
                return getDeepestCategoryName(category.categories[0]);
            }
            return category.fullCategoryName ?? null;
        };
        return getDeepestCategoryName(product?.categories?.[0]);
    };

    const previousComponent = ref(null);
    const selectedProduct = ref(null);
    const hasUserSelectedVariant = ref(false);
    const isTemplateSingleton = ref(false);
    const selectedItemCode = ref(null);
    const isOptionSpecific = ref(true);
    const selectedProductUom = ref(null);
    const bomItemDeliveryType = ref(null);
    const setSelectedProduct = async (productId, product = null) => {
        selectedProductUom.value = product?.uom ?? null;
        selectedProduct.value = null;
        previousComponent.value = componentToShow.value;
        componentToShow.value = 'ProductDetail';
        loading.value = true;
        try {
            const productDetails = await getProductDetailsById(productId);
            if (productDetails) {
                if (productDetails.selectableFeatures && productDetails.selectableFeatures.includes('N/A')) {
                    isTemplateSingleton.value = true;
                }
                let availableVariants = [];
                if (productDetails?.variants?.length > 0) {
                    availableVariants = productDetails.variants.filter(v => selectedBranchCode.value ? v.availableAtBranch : true)
                }
                productDetails.variants = availableVariants;
                // Re-resolve selectedVariant from filtered variants to carry fresh availableAtBranch
                const incomingSV = (product?.selectedVariant && typeof product.selectedVariant === 'object')
                    ? product.selectedVariant
                    : (product?.SelectedVariant && typeof product.SelectedVariant === 'object')
                        ? product.SelectedVariant
                        : null;
                const incomingColor = product?.selectedColor || product?.SelectedColor || '';
                const incomingSize = product?.selectedSize || product?.SelectedSize || '';
                const incomingCode = product?.itemCode || product?.ItemCode
                    || incomingSV?.itemCode || incomingSV?.ItemCode
                    || selectedItemCode.value;

                let resolvedVariant = null;
                if (incomingCode) {
                    resolvedVariant = availableVariants.find(v =>
                        (v.itemCode || v.ItemCode) === incomingCode
                    ) || null;
                }
                if (!resolvedVariant && (incomingColor || incomingSize)) {
                    resolvedVariant = availableVariants.find(v => {
                        const colorMatch = !incomingColor
                            || (v.colorName || v.ColorName) === incomingColor;
                        const sizeVal = v.sizeName || v.SizeName || '';
                        const agility = v.AgilitySize || v.agilitySize || '';
                        const sizeMatch = !incomingSize
                            || sizeVal === incomingSize
                            || agility === incomingSize
                            || sizeVal.toLowerCase() === String(incomingSize).toLowerCase()
                            || agility.toLowerCase() === String(incomingSize).toLowerCase();
                        return colorMatch && sizeMatch;
                    }) || null;
                }
                if (!resolvedVariant && incomingSV) {
                    const svCode = incomingSV.itemCode || incomingSV.ItemCode;
                    resolvedVariant = svCode
                        ? availableVariants.find(v => (v.itemCode || v.ItemCode) === svCode) || null
                        : null;
                }
                if (!resolvedVariant && availableVariants.length === 1) {
                    resolvedVariant = availableVariants[0];
                }
                productDetails.selectedVariant = resolvedVariant;

                // Preserve selectedColor and selectedSize from incoming product (for PDP navigation from order/template grids)
                if (incomingColor) {
                    productDetails.selectedColor = incomingColor;
                    productDetails.SelectedColor = incomingColor;
                } else if (resolvedVariant) {
                    productDetails.selectedColor = resolvedVariant.colorName || resolvedVariant.ColorName || null;
                    productDetails.SelectedColor = productDetails.selectedColor;
                }
                if (incomingSize) {
                    productDetails.selectedSize = incomingSize;
                    productDetails.SelectedSize = incomingSize;
                } else if (resolvedVariant) {
                    productDetails.selectedSize = resolvedVariant.sizeName || resolvedVariant.SizeName || null;
                    productDetails.SelectedSize = productDetails.selectedSize;
                }

                selectedProduct.value = productDetails;
            } else {
                // fallback product details when product details API fails
                // Re-resolve selectedVariant against filtered variants
                if (product?.variants?.length > 0) {
                    const filtered = selectedBranchCode.value
                        ? product.variants.filter(v => v.availableAtBranch)
                        : product.variants;
                    product.variants = filtered;
                    const sv = (product.selectedVariant && typeof product.selectedVariant === 'object')
                        ? product.selectedVariant
                        : null;
                    if (sv) {
                        const svCode = sv.itemCode || sv.ItemCode;
                        const matched = svCode
                            ? filtered.find(v => (v.itemCode || v.ItemCode) === svCode)
                            : null;
                        product.selectedVariant = matched || (filtered.length > 0 ? filtered[0] : null);
                    } else if (filtered.length === 1) {
                        product.selectedVariant = filtered[0];
                    }
                }
                selectedProduct.value = product;
            }
        } catch (e) {
            console.error(e);
        } finally {
            loading.value = false;
        }
    };

    // Refresh currently selected product without changing navigation/back stack
    const refreshSelectedProduct = async () => {
        if (!selectedProduct.value?.productId) return;
        loading.value = true;
        try {
            const productDetails = await getProductDetailsById(selectedProduct.value.productId);
            if (productDetails) {
                if (productDetails.selectableFeatures && productDetails.selectableFeatures.includes('N/A')) {
                    isTemplateSingleton.value = true;
                }
                let availableVariants = [];
                if (productDetails?.variants?.length > 0) {
                    availableVariants = productDetails.variants.filter(v => selectedBranchCode.value ? v.availableAtBranch : true)
                }
                productDetails.variants = availableVariants;
                // Re-resolve selected variant from filtered array (see setSelectedProduct)
                const prevSV = (selectedProduct.value?.selectedVariant && typeof selectedProduct.value.selectedVariant === 'object')
                    ? selectedProduct.value.selectedVariant
                    : null;
                if (prevSV) {
                    const svCode = prevSV.itemCode || prevSV.ItemCode;
                    const matched = svCode
                        ? availableVariants.find(v => (v.itemCode || v.ItemCode) === svCode)
                        : null;
                    productDetails.selectedVariant = matched || (availableVariants.length > 0 ? availableVariants[0] : null);
                } else if (availableVariants.length === 1) {
                    productDetails.selectedVariant = availableVariants[0];
                }
                selectedProduct.value = productDetails;
            }
        } catch (e) {
            console.error(e);
        } finally {
            loading.value = false;
        }
    };

    const goBack = async () => {
        const prev = previousComponent.value;
        if (prev === 'Products') {
            componentToShow.value = 'Products';
            // Load products if not already loaded
            if (!products?.value?.products?.length) {
                await loadProducts(1);
            }
        } else if (prev === 'ShoppingCart') {
            // If the user came from the cart and hasn't opened the products page before, go to CatalogCategoryTemplates
            componentToShow.value = 'ShoppingCart';
        } else {
            componentToShow.value = 'CatalogCategoryTemplates';
        }
        // Reset previous component after navigating back
        previousComponent.value = null;
    };

    const deleteItemFromCart = async (index) => {
        if (index < 0 || index >= globalCartItems.value.length) {
            console.warn('Invalid index');
            return;
        }
        cartItems.value.splice(index, 1);
        globalCart.value.cartDetails.splice(index, 1);
        await globalCartService.saveUserCart(globalCart.value);
    };

    const deleteCart = async () => {
        loading.value = true;
        try {
            const response = await globalCartService.deleteUserCart();
            if (!response.success || !response.response?.Data) {
                console.warn("The cart may not have been deleted.");
            }
            // Clear the local cart state
            globalCart.value = {
                branchCode: '',
                cartDetails: [],
                cardModified: null,
                cardCreated: null,
                cartId: null,
                customerAccount: userStore.customerCode,
                shipToSequenceNumber: selectedJobAccount.value.ShipToSequenceId,
                sourceSystem: 0, // ROOFHUB
            };
            cartItems.value = [];
        } catch (e) {
            console.warn('The user\'s cart was not deleted successfully due to an error.', e);
        }
        loading.value = false;
    };

    const existingScrollAmount = ref(null)

    // External cart for BOM
    const externalCartForBOM = ref([]);
    const isOpenCatalogSlider = ref(false);
    const loadingProductInBOM = ref(false);
    
    // Current tier ID for estimate flows (null for non-estimate flows like Create Order)
    const currentTierId = ref(null);

    // Computed: Get cart items filtered by current tier
    const cartItemsForCurrentTier = computed(() => {
        return externalCartForBOM.value.filter(item => {
            if (currentTierId.value !== null) {
                // Estimate flow: only include items for the current tier
                return item.tierId === currentTierId.value;
            } else {
                // Non-estimate flow (Order): only include items without tierId
                return item.tierId === null || item.tierId === undefined;
            }
        });
    });

    // Optional callbacks for BOM hosts that react to catalog adds/removes without watching the cart
    let bomCartHandlers = null;

    const registerBomCartHandlers = (handlers) => {
        bomCartHandlers = handlers;
        return () => {
            if (bomCartHandlers === handlers) {
                bomCartHandlers = null;
            }
        };
    };

    const addItemToExternalCartForBOM = (item) => {
        externalCartForBOM.value = externalCartForBOM.value.concat(item);
        bomCartHandlers?.onAdd?.(item);
    };

    // Method: Remove item from BOM for current tier
    const removeItemFromBOMForCurrentTier = (itemIdentifier, identifierType = 'itemCode') => {
        const previousCart = externalCartForBOM.value;
        externalCartForBOM.value = externalCartForBOM.value.filter(item => {
            // Keep items from other tiers
            if (currentTierId.value !== null) {
                // Estimate flow: keep items from different tiers
                if (item.tierId !== currentTierId.value) {
                    return true;
                }
            } else {
                // Non-estimate flow: keep items with a tierId (they belong to estimates)
                if (item.tierId !== null && item.tierId !== undefined) {
                    return true;
                }
            }
            
            // Remove matching item from current tier/context
            if (identifierType === 'itemCode') {
                const itemCode = item.itemCode || item.ItemCode;
                return itemCode !== itemIdentifier;
            } else if (identifierType === 'id') {
                return item.id !== itemIdentifier;
            }
            return true;
        });

        if (bomCartHandlers?.onRemove) {
            const removedItems = previousCart.filter(item => !externalCartForBOM.value.includes(item));
            if (removedItems.length) {
                bomCartHandlers.onRemove(removedItems);
            }
        }
    };

    // Method: Check if item exists in BOM for current tier
    const isItemInBOMForCurrentTier = (itemCode) => {
        if (itemCode == null || String(itemCode).trim() === '') return false
        return cartItemsForCurrentTier.value.some(item => {
            const code = item.itemCode || item.ItemCode;
            return !!code && code === itemCode;
        });
    };

    const integrationPopupItem = ref(null)

    return {
        error,
        loading,
        componentToShow,
        getEligibleBranches,
        userRoles,
        branches,
        selectedBranch,
        setSelectedBranch,
        selectedBranchCode,
        loadRoofHubCategories,
        categories,
        getSelectedCategories,
        selectedCategories,
        setIsSelectedForCategories,
        getManufacturers,
        manufacturers,
        selectedManufacturers,
        selectedTemplates,
        listTypes,
        selectedListTypes,
        colorOptions,
        selectedColors,
        sizeOptions,
        selectedSizes,
        products,
        facetCounts,
        getFacetCount,
        getCategoryProductCount,
        getManufacturerProductCount,
        loadFacetCounts,
        clearFacetCounts,
        productsPricing,
        productsPerPage,
        productSearchTerm,
        branchesReady,
        clearProductSearch,
        getProductPricing,
        getBatchProductPricing,
        clearAllFilters,
        clearSelectedFilters,

        getCartItems,
        globalCartItems,
        addItemToCart,
        deleteCart,

        cartItems,
        fetchCartItems,
        loadProducts,
        inventoryStatusRequestDate,
        inventoryRequestsEnabled,
        productInventoryLevelStatus,
        getProductInventoryLevel,
        suspendInventoryRequests,
        resumeInventoryRequests,
        goBack,
        existingScrollAmount,
        jobAccounts,
        customerShipToAddresses,
        fetchJobAccounts,
        selectedJobAccount,

        setSelectedProduct,
        selectedProduct,
        selectedProductUom,
        hasUserSelectedVariant,
        deleteItemFromCart,

        externalCartForBOM,
        isOpenCatalogSlider,
        loadingProductInBOM,
        currentTierId,
        cartItemsForCurrentTier,
        removeItemFromBOMForCurrentTier,
        registerBomCartHandlers,
        addItemToExternalCartForBOM,
        isItemInBOMForCurrentTier,
        isTemplateSingleton,
        selectedItemCode,
        isOptionSpecific,
        bomItemDeliveryType,
        suppressExternalCartBranchRefresh,

        integrationPopupItem
    }
})